import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";

import { cn } from "../../lib/utils";

const Accordion = AccordionPrimitive.Root;

interface AccordionItemProps {
  children?: React.ReactNode;
  className?: string;
  value?: string;
}

/** @type {React.ForwardRefExoticComponent<AccordionItemProps & React.ComponentProps<typeof AccordionPrimitive.Item>>} */
const AccordionItem = React.forwardRef<
  HTMLDivElement,
  AccordionItemProps & React.ComponentProps<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item ref={ref} className={cn("border-b", className)} {...props} />
));

interface AccordionTriggerProps {
  children?: React.ReactNode;
  className?: string;
}

/** @type {React.ForwardRefExoticComponent<AccordionTriggerProps & React.ComponentProps<typeof AccordionPrimitive.Trigger>>} */
const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  AccordionTriggerProps & React.ComponentProps<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
        className
      )}
      {...props}
    >
      {children}
      <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));

interface AccordionContentProps {
  children?: React.ReactNode;
  className?: string;
}

/** @type {React.ForwardRefExoticComponent<AccordionContentProps & React.ComponentProps<typeof AccordionPrimitive.Content>>} */
const AccordionContent = React.forwardRef<
  HTMLDivElement,
  AccordionContentProps & React.ComponentProps<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionItem.displayName = AccordionPrimitive.Item.displayName;
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
